import { FiChevronRight } from "react-icons/fi";
import { FaRegPaperPlane, FaInbox } from "react-icons/fa";
import { MdLayers } from "react-icons/md";
import React from "react";

export function RenderMsgMQ () {
    return (
  <div className="transition-opacity duration-300">
    <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-1">
      <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
      <FiChevronRight className="text-[14px]" />
      <span>Product Capabilities</span>
      <FiChevronRight className="text-[14px]" />
      <span className="text-[#1b140d] dark:text-white font-medium">Message Queue Monitoring</span>
    </nav>

    <main className="py-10 space-y-9">

      {/* Header Section */}
      <section className="mb-9">
        <h1 className="text-5xl mb-3 md:text-5xl font-bold leading-snug text-[#1b140d] dark:text-white">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
            Message Queue Monitoring
          </span>
          <br />
          Ensuring Reliability for Event-Driven Architectures
        </h1>
        <p className="text-base md:text-lg text-[#4b5563] dark:text-gray-300 leading-relaxed">
          Message queues are the backbone of modern microservices. Netraa provides deep-dive monitoring to visualize data flows, detect backpressure, and ensure your event-driven systems remain reliable under load.
        </p>
      </section>

{/* Section 1: ActiveMQ */}
<section className="w-full space-y-6 scroll-mt-24" id="activemq">
  <h3 className="text-3xl font-bold text-text-main dark:text-white">
    ActiveMQ Monitoring: The Traditional Backbone
  </h3>
  <p className="text-sm text-text-muted dark:text-gray-300">
    For traditional enterprise service buses, visibility into the Broker and Destination levels is critical. Netraa connects directly to your ActiveMQ brokers via JMX or native plugins to extract real-time metrics.
  </p>

  {/* Dashboard Mockup */}
  <div className="rounded-xl border border-border-color dark:border-border-dark bg-surface-light dark:bg-surface-dark shadow-sm overflow-hidden p-6 space-y-6">
    <div className="grid md:grid-cols-2 gap-6">
      
      {/* JVM Heap Usage */}
      <div className="space-y-2">
        <div className="flex justify-between items-end">
          <span className="text-sm font-medium text-text-muted dark:text-gray-400">JVM Heap Usage</span>
          <span className="text-lg font-bold text-text-main dark:text-white">72%</span>
        </div>
        <div className="h-24 w-full bg-surface-light dark:bg-surface-dark rounded-lg relative px-2 py-2">
  <svg
    className="w-full h-full text-primary/20 fill-primary/20 stroke-primary"
    preserveAspectRatio="none"
    viewBox="0 0 100 40"
  >
    <path
      d="M0,24 Q10,22 20,29 T40,19 T60,26 T80,16 T100,22 V40 H0 Z"
      strokeWidth="2"
      vectorEffect="non-scaling-stroke"
    />
  </svg>
</div>

      </div>

      {/* Queue Depth */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-text-muted dark:text-gray-400">Queue Depth (Top 3)</h4>
        {[
          { name: "orders.new", count: 452, color: "bg-primary", width: "w-[75%]" },
          { name: "notifications.email", count: 128, color: "bg-primary/60", width: "w-[30%]" },
          { name: "audit.logs", count: 890, color: "bg-red-500", width: "w-[90%]" },
        ].map((queue) => (
          <div key={queue.name} className="space-y-1">
            <div className="flex justify-between text-xs text-text-muted dark:text-gray-400">
              <span>{queue.name}</span>
              <span>{queue.count} msgs</span>
            </div>
            <div className="h-2 w-full bg-surface-light dark:bg-surface-dark rounded-full overflow-hidden">
              <div className={`${queue.color} ${queue.width} h-full rounded-full`}></div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Pro Tip */}
    <div className="flex gap-4 p-5 rounded-xl bg-orange-50 dark:bg-orange-900/10 border-l-4 border-primary">
      <div className="text-primary mt-1">
        <span className="material-symbols-outlined">lightbulb</span>
      </div>
      <div>
        <h4 className="text-base font-bold text-text-main dark:text-white mb-1">
          Pro Tip: Dead Letter Queues (DLQ)
        </h4>
        <p className="text-sm text-text-muted dark:text-gray-300 leading-relaxed">
          Always configure alerts for your DLQs. A growing Dead Letter Queue is an early warning sign of poison messages or consumer processing failures.
        </p>
      </div>
    </div>
  </div>
</section>


      {/* Section 2: Kafka */}
      <section className="w-full space-y-6 scroll-mt-24" id="kafka">
        <h3 className="text-3xl font-bold">Kafka Monitoring: High-Throughput Stream Intelligence</h3>
        <p className="text-[#4b5563] dark:text-gray-300">
          Apache Kafka requires a different mindset. It's not just about queue depth, but about <strong>Consumer Lag</strong> and <strong>Partition Health</strong>. Netraa visualizes your entire cluster topology.
        </p>
        {/* Kafka Visual */}
        <div className="rounded-xl border border-[#f3ede7] dark:border-white/10 bg-[#1e293b] text-white shadow-lg overflow-hidden p-6">
          <h4 className="font-bold text-lg mb-4">Cluster: prod-kafka-01</h4>
          <div className="flex items-start gap-8 mb-4">
            {["Broker-1", "Broker-2", "Broker-3"].map((broker) => (
              <div key={broker} className="flex flex-col items-start gap-2">
                <div className="size-12 rounded-full bg-blue-600 flex items-center justify-center shadow-lg ring-4 ring-blue-900 border border-blue-400">
                  <span className="material-symbols-outlined text-sm">dns</span>
                </div>
                <span className="text-xs text-blue-200">{broker}</span>
              </div>
            ))}
          </div>
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <span className="text-sm font-medium text-slate-300">Consumer Lag (Group: payment-processor)</span>
            <div className="flex items-end h-24 gap-1 mt-2">
              {[20, 35, 25, 50, 40, 15, 10].map((h, i) => (
                <div key={i} className={`w-full bg-primary/20 h-[${h}%] rounded-t-sm`}></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Netraa Advantage */}
<section className="w-full space-y-6 scroll-mt-24" id="advantage">

  <h3 className="text-3xl font-bold">
    The Netraa Advantage: Cross-Layer Correlation
  </h3>

  <p className="text-[#4b5563] dark:text-gray-300 max-w-3xl leading-relaxed">
    Standalone metrics only tell half the story. Netraa's distributed tracing
    automatically stitches together the Producer, the Broker hop, and the
    Consumer action into a single trace.
  </p>

  {/* Correlation Flow Diagram */}
  <div className="
    rounded-xl
    border border-dashed border-gray-300 dark:border-gray-600
    bg-gray-50 dark:bg-white/5
    p-6 md:p-8
  ">

    <div className="
      flex flex-col md:flex-row
      items-center
      justify-center
      gap-6 md:gap-4
      w-full
    ">

      {[
        {
          label: "Producer App",
          icon: <FaRegPaperPlane size={24} />,
        },
        {
          label: "Message Queue (Hop)",
          icon: <MdLayers size={24} />,
          highlight: true,
        },
        {
          label: "Consumer App",
          icon: <FaInbox size={24} />,
        },
      ].map((item, i, arr) => (
        <React.Fragment key={i}>

          {/* CARD */}
          <div className="flex flex-col items-center relative z-10">

            <div
              className={`
                w-40 h-24
                rounded-xl
                shadow-sm
                flex flex-col items-center justify-center
                gap-2
                px-4 py-3
                transition-all duration-300

                ${
                  item.highlight
                    ? "bg-primary/10 border-2 border-primary"
                    : "bg-white dark:bg-black/40 border border-gray-200 dark:border-white/10"
                }
              `}
            >

              {/* latency badge */}
              {item.highlight && (
                <div className="
                  absolute
                  -top-3
                  px-2 py-0.5
                  bg-primary
                  text-white
                  text-[10px]
                  rounded-full
                  font-bold
                  shadow-sm
                ">
                  Latency: 2ms
                </div>
              )}

              <div className={item.highlight ? "text-primary" : ""}>
                {item.icon}
              </div>

              <span
                className={`
                  text-xs font-bold text-center leading-snug

                  ${item.highlight ? "text-primary" : ""}
                `}
              >
                {item.label}
              </span>
            </div>
          </div>

          {/* CONNECTOR */}
          {i < arr.length - 1 && (
            <div className="
              flex items-center justify-center

              w-0.5 h-8
              md:w-12 md:h-0.5

              bg-gray-300 dark:bg-gray-600

              relative
            ">

              {/* animated pulse */}
              <div className="
                absolute
                w-2 h-2
                rounded-full
                bg-primary

                animate-pulse
              "></div>
            </div>
          )}

        </React.Fragment>
      ))}
    </div>
  </div>
</section>
    </main>
  </div>
);
}